/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Select,
  Button,
  Group,
  Text,
  Box,
  Loader,
  Stack,
  NumberInput,
  SegmentedControl,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useGetOrganizations } from "api/superAdmin/organizationManagement";
import { useUser } from "hooks/useUser";
import { useAdminUpdateLeave } from "api/newLeave/adduserBalance";
import { useGetUserLeaveBalance } from "api/newLeave/getuserbalancebyId";
import { useGetAllOrgMembers } from "api/organizations/getAllOrgMembers";

const formatBalance = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return "0";
  const n = Number(num);
  if (Number.isNaN(n)) return "0";
  if (Number.isInteger(n)) return n.toString();
  return n.toFixed(2);
};

export default function UserBalanceUpdateModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { user } = useUser();
  const { data: orgMembers } = useGetAllOrgMembers(user?.org?._id);
  const { mutate: updateLeave, isLoading } = useAdminUpdateLeave();
  const { isLoading: orgsLoading } = useGetOrganizations(1, 10);

  const [selectedMember, setSelectedMember] = useState<string>("");

  const { data: userBalanceResponse, isLoading: userBalanceLoading } =
    useGetUserLeaveBalance(selectedMember);

  const form = useForm({
    initialValues: {
      member: "",
      leaveType: "",
      action: "increase",
      amount: 1,
    },
    validate: {
      member: (val) => (val ? null : "Please select a member"),
      leaveType: (val) => (val ? null : "Please select a leave type"),
      amount: (val) => (val && Number(val) > 0 ? null : "Enter amount > 0"),
    },
  });

  const [balance, setBalance] = useState<number | null>(null);

  const members = useMemo(() => {
    const base = orgMembers ?? [];
    const currentUserId = String(user?._id ?? "");
    return base
      .filter((u: any) => {
        const memberId = String(u._id ?? u.id ?? "");
        return memberId !== currentUserId;
      })
      .map((u: any) => ({
        value: String(u._id ?? u.id ?? ""),
        label: u.firstName
          ? `${u.firstName} ${u.lastName ?? ""}`
          : u.email ?? "Unknown",
      }));
  }, [orgMembers, user]);

  const leaveTypeOptions = [
    { value: "annual", label: "Annual Leave" },
    { value: "sick", label: "Sick Leave" },
    { value: "pressingNecessity", label: "Pressing Necessity" },
    { value: "compassionate", label: "Compassionate Leave" },
    { value: "bereavement", label: "Bereavement Leave" },
    { value: "juryDuty", label: "Jury Duty" },
    { value: "election", label: "Election Leave" },
    { value: "bandBusiness", label: "Band Business Leave" },
    { value: "governance", label: "Governance Leave" },
    { value: "education", label: "Education Leave" },
    { value: "professionalDevelopment", label: "Professional Development" },
    { value: "culturalSpiritual", label: "Cultural & Spiritual Leave" },
    { value: "leaveWithoutPay", label: "Leave Without Pay (LWP)" },
  ];

  const apiLeaveTypeMap: Record<string, string> = {
    annual: "annualLeave",
    sick: "sickLeave",
    pressingNecessity: "pressingNecessity",
    compassionate: "compassionate",
    bereavement: "bereavement",
    juryDuty: "juryDuty",
    election: "election",
    bandBusiness: "bandBusiness",
    governance: "governance",
    education: "education",
    professionalDevelopment: "professionalDevelopment",
    culturalSpiritual: "culturalSpiritual",
    leaveWithoutPay: "leaveWithoutPay",
  };

  useEffect(() => {
    setSelectedMember(form.values.member || "");
  }, [form.values.member]);

  useEffect(() => {
    if (!form.values.leaveType) {
      setBalance(null);
      return;
    }

    const balances = (userBalanceResponse as any)?.data?.balances;
    if (!balances) {
      setBalance(0);
      return;
    }

    const apiKey = apiLeaveTypeMap[form.values.leaveType];
    const leaveObj = balances[apiKey];

    setBalance(typeof leaveObj?.balance === "number" ? leaveObj.balance : 0);
  }, [form.values.leaveType, userBalanceResponse]);

  const handleSubmit = (values: any) => {
    const memberId = values.member;
    const leaveTypeKey = apiLeaveTypeMap[values.leaveType];
    const action = values.action === "increase" ? "increase" : "decrease";
    const amount = Number(values.amount) || 0;

    if (!memberId) return;

    updateLeave(
      {
        id: memberId,
        payload: { leaveType: leaveTypeKey, action, amount },
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Success",
            message: "Balance updated",
            color: "green",
          });
          onClose();
        },
        onError: (err: any) => {
          showNotification({
            title: "Error",
            message: err?.message || "Update failed",
            color: "red",
          });
        },
      }
    );
  };

  const afterBalance = (() => {
    const amt = Number(form.values.amount) || 0;
    const current = balance ?? 0;
    return form.values.action === "decrease" ? current - amt : current + amt;
  })();

  if (orgsLoading) {
    return (
      <Box p="xl" style={{ display: "flex", justifyContent: "center" }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Update User Balance"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="md">
          <Select
            label="Select Member"
            data={members}
            searchable
            {...form.getInputProps("member")}
          />

          <Select
            label="Leave Type"
            data={leaveTypeOptions}
            {...form.getInputProps("leaveType")}
          />

          <SegmentedControl
            fullWidth
            value={form.values.action}
            onChange={(v) => form.setFieldValue("action", v)}
            data={[
              { label: "Increase", value: "increase" },
              { label: "Decrease", value: "decrease" },
            ]}
          />

          <NumberInput
            label="Amount"
            min={0}
            step={0.5}
            precision={2}
            {...form.getInputProps("amount")}
          />

          <Box p="sm" sx={{ border: "1px solid #e9ecef", borderRadius: 6 }}>
            <Group position="apart">
              <Text weight={600}>Current Balance</Text>
              {userBalanceLoading ? (
                <Loader size={18} />
              ) : (
                <Text>{formatBalance(balance)} days</Text>
              )}
            </Group>

            <Group position="apart" mt="sm">
              <Text weight={600}>After Update Balance</Text>
              <Text>{formatBalance(afterBalance)} days</Text>
            </Group>
          </Box>

          <Group position="right">
            <Button color="cancel" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              Update
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
